import type { ListingsAPIAndFilter, ListingsAPIOrFilter } from '@noths/polaris-client-listings-api';
import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import { taxonomyAttributeToFacetKey } from 'src/adapters/pageURLToStore/shop/constants';
import type { ShopTaxonomyLevels } from 'src/adapters/pageURLToStore/shop/shopTaxonomyPath';
import { requestProducts } from 'src/redux/products/thunks/requestProducts';
import type { ListingsAPIServingConfig } from 'src/services/listings-api/servingConfigs';
import { applyShopTaxonomyToFacets } from './helpers/shop/applyShopTaxonomyToFacets';
import type { Facet } from './types/Facet';
import type { SortOption } from './types/SortOption';

export interface ProductsQueryState {
  canonicalFilters?: ListingsAPIAndFilter | ListingsAPIOrFilter;
  facets: Facet[];
  isModalOpen: boolean;
  page: number;
  pageCategories?: string[];
  searchTerm?: string;
  servingConfig?: ListingsAPIServingConfig;
  sortOptions: SortOption[];
  taxonomyAttributes?: string[];
}

export interface SyncProductsQueryFromURLPayload {
  activeFacetValuesByKey: Record<string, string[]>;
  activeSort: { descending: boolean; key: string } | null;
  page: number;
  searchTerm?: string;
  shopTaxonomyLevels?: ShopTaxonomyLevels;
}

export const initialState: Readonly<ProductsQueryState> = {
  facets: [],
  isModalOpen: false,
  page: 1,
  sortOptions: [],
};

interface ApplyActiveFacetValuesOptions {
  skipKeys?: ReadonlySet<string>;
}

export const applyActiveFacetValuesToFacets = (
  facets: Facet[],
  activeFacetValuesByKey: Record<string, string[]>,
  { skipKeys }: ApplyActiveFacetValuesOptions = {},
): Facet[] =>
  facets.map((facet) => {
    if (skipKeys?.has(facet.key)) {
      return facet;
    }

    const activeValues = new Set(activeFacetValuesByKey[facet.alias] ?? []);

    return {
      ...facet,
      options: facet.options.map((option) => ({
        ...option,
        active: activeValues.has(option.value),
      })),
    };
  });

export const applyActiveSortToSortOptions = (
  sortOptions: SortOption[],
  activeSort: SyncProductsQueryFromURLPayload['activeSort'],
): SortOption[] =>
  sortOptions.map((option) => ({
    ...option,
    active:
      activeSort != null &&
      option.key === activeSort.key &&
      option.descending === activeSort.descending,
  }));

const taxonomyFacetKeysFor = (
  taxonomyAttributes: string[] | undefined,
): ReadonlySet<string> | undefined =>
  taxonomyAttributes && taxonomyAttributes.length > 0
    ? new Set(taxonomyAttributes.map(taxonomyAttributeToFacetKey))
    : undefined;

export const applyURLActiveStateToProductsQuery = (
  productsQuery: ProductsQueryState,
  active: SyncProductsQueryFromURLPayload,
): ProductsQueryState => {
  const taxonomyKeys = taxonomyFacetKeysFor(productsQuery.taxonomyAttributes);

  const facetsWithTaxonomy =
    active.shopTaxonomyLevels && productsQuery.taxonomyAttributes
      ? applyShopTaxonomyToFacets(
          productsQuery.facets,
          active.shopTaxonomyLevels,
          productsQuery.taxonomyAttributes,
        )
      : productsQuery.facets;

  return {
    ...productsQuery,
    facets: applyActiveFacetValuesToFacets(facetsWithTaxonomy, active.activeFacetValuesByKey, {
      skipKeys: taxonomyKeys,
    }),
    sortOptions: applyActiveSortToSortOptions(productsQuery.sortOptions, active.activeSort),
    page: active.page,
    searchTerm: active.searchTerm,
  };
};

export const { actions, reducer } = createSlice({
  name: 'productsQuery',
  initialState,
  reducers: {
    closeModal: (state) => {
      state.isModalOpen = false;
    },
    openModal: (state) => {
      state.isModalOpen = true;
    },
    changePage: (state, action: PayloadAction<number>) => {
      state.page = action.payload;
    },
    syncFromURL: (state, action: PayloadAction<SyncProductsQueryFromURLPayload>) => {
      const { searchTerm } = action.payload;

      const nextState = applyURLActiveStateToProductsQuery(state, action.payload);

      state.facets = nextState.facets;
      state.sortOptions = nextState.sortOptions;
      state.page = nextState.page;

      // Keep state JSON-serializable for SSR by removing absent search terms.
      if (searchTerm === undefined) {
        delete state.searchTerm;
      } else {
        state.searchTerm = searchTerm;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(requestProducts.fulfilled, (state, { payload }) => {
      if (payload.sortOptions) {
        state.sortOptions = payload.sortOptions;
      }

      if (payload.facets) {
        state.facets = payload.facets;
      }
    });
  },
});
